import * as THREE from 'three';
import {
  DESK_COLOR, MONITOR_FRAME_COLOR, MONITOR_SCREEN_COLOR,
  KEYBOARD_COLOR, CHAIR_SEAT_COLOR, CHAIR_BASE_COLOR,
  WHITEBOARD_COLOR, WHITEBOARD_FRAME_COLOR,
  PLANT_POT_COLOR, PLANT_LEAF_COLOR,
  WINDOW_FRAME_COLOR, WINDOW_GLASS_COLOR,
  BOOKSHELF_COLOR, BOOK_COLORS,
  MUG_COLOR, RUG_COLOR,
  COFFEE_MACHINE_COLOR, COUNTER_COLOR, WATER_COOLER_COLOR,
  MEETING_TABLE_COLOR, STOOL_COLOR,
} from '@/utils/constants';

/**
 * Factory functions for all 10 furniture items.
 * Each returns a THREE.Group with userData.id and userData.type.
 */

// ── 1. Desk ────────────────────────────────────────────────
export function createDesk(
  position: THREE.Vector3,
  rotation: number,
  id: string = 'desk-0',
): THREE.Group {
  const group = new THREE.Group();
  group.name = id;
  group.userData = { id, type: 'desk' };
  group.position.copy(position);
  group.rotation.y = rotation;

  const mat = new THREE.MeshStandardMaterial({ color: DESK_COLOR });

  // Tabletop
  const topGeo = new THREE.BoxGeometry(1.8, 0.05, 0.7);
  const top = new THREE.Mesh(topGeo, mat);
  top.position.y = 1.0;
  top.castShadow = true;
  top.receiveShadow = true;
  group.add(top);

  // 4 Legs
  const legGeo = new THREE.CylinderGeometry(0.03, 0.03, 1.0, 6);
  const legPositions: [number, number][] = [
    [-0.85, -0.3], [-0.85, 0.3], [0.85, -0.3], [0.85, 0.3],
  ];
  legPositions.forEach(([x, z]) => {
    const leg = new THREE.Mesh(legGeo, mat);
    leg.position.set(x, 0.5, z);
    leg.castShadow = true;
    group.add(leg);
  });

  return group;
}

// ── 2. Monitor ─────────────────────────────────────────────
export function createMonitor(
  position: THREE.Vector3,
  id: string = 'monitor-0',
): THREE.Group {
  const group = new THREE.Group();
  group.name = id;
  group.userData = { id, type: 'prop' };
  group.position.copy(position);

  // Screen panel — bright emissive so it reads as "ON" from any angle
  const screenGeo = new THREE.BoxGeometry(0.7, 0.45, 0.02);
  const screenMat = new THREE.MeshStandardMaterial({
    color: MONITOR_SCREEN_COLOR,
    emissive: MONITOR_SCREEN_COLOR,
    emissiveIntensity: 1.2,
    roughness: 0.1,
    side: THREE.DoubleSide,
  });
  const screen = new THREE.Mesh(screenGeo, screenMat);
  screen.position.y = 0.3;
  group.add(screen);

  // Frame (slightly larger behind screen)
  const frameGeo = new THREE.BoxGeometry(0.74, 0.49, 0.03);
  const frameMat = new THREE.MeshStandardMaterial({ color: MONITOR_FRAME_COLOR });
  const frame = new THREE.Mesh(frameGeo, frameMat);
  frame.position.set(0, 0.3, -0.01);
  group.add(frame);

  // Stand neck
  const neckGeo = new THREE.CylinderGeometry(0.02, 0.02, 0.15, 6);
  const neck = new THREE.Mesh(neckGeo, frameMat);
  neck.position.y = 0.05;
  group.add(neck);

  // Stand base
  const baseGeo = new THREE.CylinderGeometry(0.1, 0.1, 0.02, 12);
  const base = new THREE.Mesh(baseGeo, frameMat);
  base.position.y = -0.02;
  group.add(base);

  return group;
}

// ── 3. Keyboard ────────────────────────────────────────────
export function createKeyboard(
  position: THREE.Vector3,
  id: string = 'keyboard-0',
): THREE.Group {
  const group = new THREE.Group();
  group.name = id;
  group.userData = { id, type: 'prop' };
  group.position.copy(position);

  const geo = new THREE.BoxGeometry(0.35, 0.02, 0.12);
  const mat = new THREE.MeshStandardMaterial({ color: KEYBOARD_COLOR, roughness: 0.6 });
  const keyboard = new THREE.Mesh(geo, mat);
  group.add(keyboard);

  return group;
}

// ── 4. Chair ───────────────────────────────────────────────
export function createChair(
  position: THREE.Vector3,
  id: string = 'chair-0',
): THREE.Group {
  const group = new THREE.Group();
  group.name = id;
  group.userData = { id, type: 'furniture' };
  group.position.copy(position);

  const seatMat = new THREE.MeshStandardMaterial({ color: CHAIR_SEAT_COLOR });
  const baseMat = new THREE.MeshStandardMaterial({ color: CHAIR_BASE_COLOR, metalness: 0.8, roughness: 0.3 });

  // Seat
  const seatGeo = new THREE.BoxGeometry(0.45, 0.05, 0.45);
  const seat = new THREE.Mesh(seatGeo, seatMat);
  seat.position.y = 0.5;
  seat.castShadow = true;
  group.add(seat);

  // Backrest
  const backGeo = new THREE.BoxGeometry(0.43, 0.5, 0.04);
  const back = new THREE.Mesh(backGeo, seatMat);
  back.position.set(0, 0.75, -0.2);
  back.rotation.x = -0.1;
  back.castShadow = true;
  group.add(back);

  // Base cylinder
  const baseGeo = new THREE.CylinderGeometry(0.04, 0.04, 0.35, 8);
  const base = new THREE.Mesh(baseGeo, baseMat);
  base.position.y = 0.3;
  group.add(base);

  // Wheel base (star)
  const wheelBaseGeo = new THREE.CylinderGeometry(0.2, 0.2, 0.02, 5);
  const wheelBase = new THREE.Mesh(wheelBaseGeo, baseMat);
  wheelBase.position.y = 0.12;
  group.add(wheelBase);

  // Wheel nubs
  for (let i = 0; i < 5; i++) {
    const angle = (i / 5) * Math.PI * 2;
    const wheelGeo = new THREE.SphereGeometry(0.025, 6, 6);
    const wheel = new THREE.Mesh(wheelGeo, baseMat);
    wheel.position.set(Math.cos(angle) * 0.19, 0.1, Math.sin(angle) * 0.19);
    group.add(wheel);
  }

  return group;
}

// ── 5. Whiteboard ──────────────────────────────────────────
export function createWhiteboard(
  position: THREE.Vector3,
  id: string = 'whiteboard',
): THREE.Group {
  const group = new THREE.Group();
  group.name = id;
  group.userData = { id, type: 'furniture' };
  group.position.copy(position);

  // Frame (2× size)
  const frameGeo = new THREE.BoxGeometry(3.12, 2.12, 0.04);
  const frameMat = new THREE.MeshStandardMaterial({ color: WHITEBOARD_FRAME_COLOR });
  const frame = new THREE.Mesh(frameGeo, frameMat);
  group.add(frame);

  // White surface (2× size)
  const surfaceGeo = new THREE.BoxGeometry(3.0, 2.0, 0.02);
  const surfaceMat = new THREE.MeshStandardMaterial({ color: WHITEBOARD_COLOR, roughness: 0.3 });
  const surface = new THREE.Mesh(surfaceGeo, surfaceMat);
  surface.position.z = 0.02;
  group.add(surface);

  // Marker tray (2× width)
  const trayGeo = new THREE.BoxGeometry(1.2, 0.03, 0.06);
  const tray = new THREE.Mesh(trayGeo, frameMat);
  tray.position.set(0, -1.06, 0.04);
  group.add(tray);

  // Rotate to face right (away from left wall)
  group.rotation.y = Math.PI / 2;

  return group;
}

// ── 6. Plant ───────────────────────────────────────────────
export function createPlant(
  position: THREE.Vector3,
  id: string = 'plant-0',
): THREE.Group {
  const group = new THREE.Group();
  group.name = id;
  group.userData = { id, type: 'prop' };
  group.position.copy(position);

  // Pot
  const potGeo = new THREE.CylinderGeometry(0.12, 0.1, 0.15, 8);
  const potMat = new THREE.MeshStandardMaterial({ color: PLANT_POT_COLOR });
  const pot = new THREE.Mesh(potGeo, potMat);
  pot.position.y = 0.075;
  pot.castShadow = true;
  group.add(pot);

  // Foliage cluster (3 spheres)
  const leafMat = new THREE.MeshStandardMaterial({ color: PLANT_LEAF_COLOR });
  const leafPositions: [number, number, number, number][] = [
    [0, 0.25, 0, 0.1],
    [-0.06, 0.2, 0.04, 0.07],
    [0.05, 0.22, -0.03, 0.08],
  ];
  leafPositions.forEach(([x, y, z, r]) => {
    const leafGeo = new THREE.SphereGeometry(r, 8, 8);
    const leaf = new THREE.Mesh(leafGeo, leafMat);
    leaf.position.set(x, y, z);
    leaf.castShadow = true;
    group.add(leaf);
  });

  return group;
}

// ── 7. Window ──────────────────────────────────────────────
export function createWindow(
  position: THREE.Vector3,
  id: string = 'window',
): THREE.Group {
  const group = new THREE.Group();
  group.name = id;
  group.userData = { id, type: 'furniture' };
  group.position.copy(position);

  // Frame
  const frameGeo = new THREE.BoxGeometry(1.06, 1.26, 0.06);
  const frameMat = new THREE.MeshStandardMaterial({ color: WINDOW_FRAME_COLOR });
  const frame = new THREE.Mesh(frameGeo, frameMat);
  group.add(frame);

  // Glass pane
  const glassGeo = new THREE.PlaneGeometry(1.0, 1.2);
  const glassMat = new THREE.MeshStandardMaterial({
    color: WINDOW_GLASS_COLOR,
    transparent: true,
    opacity: 0.3,
    roughness: 0.1,
  });
  const glass = new THREE.Mesh(glassGeo, glassMat);
  glass.position.z = 0.035;
  group.add(glass);

  // Cross bars
  const barMat = new THREE.MeshStandardMaterial({ color: WINDOW_FRAME_COLOR });
  const hBarGeo = new THREE.BoxGeometry(1.0, 0.03, 0.02);
  const hBar = new THREE.Mesh(hBarGeo, barMat);
  hBar.position.z = 0.04;
  group.add(hBar);

  const vBarGeo = new THREE.BoxGeometry(0.03, 1.2, 0.02);
  const vBar = new THREE.Mesh(vBarGeo, barMat);
  vBar.position.z = 0.04;
  group.add(vBar);

  return group;
}

// ── 8. Bookshelf ───────────────────────────────────────────
export function createBookshelf(
  position: THREE.Vector3,
  id: string = 'bookshelf',
): THREE.Group {
  const group = new THREE.Group();
  group.name = id;
  group.userData = { id, type: 'furniture' };
  group.position.copy(position);

  const woodMat = new THREE.MeshStandardMaterial({ color: BOOKSHELF_COLOR });

  // Back panel
  const backGeo = new THREE.BoxGeometry(0.8, 2.0, 0.02);
  const back = new THREE.Mesh(backGeo, woodMat);
  back.position.set(0, 1.0, -0.14);
  back.castShadow = true;
  group.add(back);

  // Side panels
  const sideGeo = new THREE.BoxGeometry(0.02, 2.0, 0.3);
  [-0.39, 0.39].forEach((x) => {
    const side = new THREE.Mesh(sideGeo, woodMat);
    side.position.set(x, 1.0, 0);
    side.castShadow = true;
    group.add(side);
  });

  // Shelves (4 horizontal)
  const shelfGeo = new THREE.BoxGeometry(0.76, 0.02, 0.28);
  const shelfHeights = [0.02, 0.5, 1.0, 1.5, 1.98];
  shelfHeights.forEach((y) => {
    const shelf = new THREE.Mesh(shelfGeo, woodMat);
    shelf.position.set(0, y, 0);
    group.add(shelf);
  });

  // Books on shelves
  let bookIndex = 0;
  [0.55, 1.05, 1.55].forEach((shelfY) => {
    const numBooks = 2 + Math.floor(Math.random() * 3);
    let xPos = -0.3;
    for (let b = 0; b < numBooks && xPos < 0.3; b++) {
      const width = 0.06 + Math.random() * 0.06;
      const height = 0.3 + Math.random() * 0.15;
      const bookGeo = new THREE.BoxGeometry(width, height, 0.18);
      const bookMat = new THREE.MeshStandardMaterial({
        color: BOOK_COLORS[bookIndex % BOOK_COLORS.length],
      });
      const book = new THREE.Mesh(bookGeo, bookMat);
      book.position.set(xPos + width / 2, shelfY + height / 2, 0);
      group.add(book);
      xPos += width + 0.02;
      bookIndex++;
    }
  });

  // Rotate to face inward from right wall
  group.rotation.y = -Math.PI / 2;

  return group;
}

// ── 9. Coffee Mug ──────────────────────────────────────────
export function createMug(
  position: THREE.Vector3,
  id: string = 'mug-0',
): THREE.Group {
  const group = new THREE.Group();
  group.name = id;
  group.userData = { id, type: 'prop' };
  group.position.copy(position);

  const mugGeo = new THREE.CylinderGeometry(0.06, 0.055, 0.08, 12);
  const mugMat = new THREE.MeshStandardMaterial({ color: MUG_COLOR, roughness: 0.4 });
  const mug = new THREE.Mesh(mugGeo, mugMat);
  mug.position.y = 0.04;
  group.add(mug);

  // Handle
  const handleGeo = new THREE.TorusGeometry(0.03, 0.008, 6, 12, Math.PI);
  const handle = new THREE.Mesh(handleGeo, mugMat);
  handle.position.set(0.065, 0.04, 0);
  handle.rotation.z = Math.PI / 2;
  group.add(handle);

  return group;
}

// ── 10. Rug ────────────────────────────────────────────────
export function createRug(
  position: THREE.Vector3,
  id: string = 'rug',
): THREE.Group {
  const group = new THREE.Group();
  group.name = id;
  group.userData = { id, type: 'furniture' };
  group.position.copy(position);

  const rugGeo = new THREE.BoxGeometry(4.0, 0.02, 3.0);
  const rugMat = new THREE.MeshStandardMaterial({ color: RUG_COLOR, roughness: 0.9 });
  const rug = new THREE.Mesh(rugGeo, rugMat);
  rug.receiveShadow = true;
  group.add(rug);

  return group;
}

// ── 11. Coffee Machine / Break Area ───────────────────────
export function createCoffeeMachine(
  position: THREE.Vector3,
  id: string = 'coffee-machine',
): THREE.Group {
  const group = new THREE.Group();
  group.name = id;
  group.userData = { id, type: 'furniture' };
  group.position.copy(position);

  const counterMat = new THREE.MeshStandardMaterial({ color: COUNTER_COLOR });
  const machineMat = new THREE.MeshStandardMaterial({ color: COFFEE_MACHINE_COLOR });
  const coolerMat = new THREE.MeshStandardMaterial({
    color: WATER_COOLER_COLOR,
    transparent: true,
    opacity: 0.6,
  });

  // Counter surface
  const counterGeo = new THREE.BoxGeometry(0.6, 0.8, 0.35);
  const counter = new THREE.Mesh(counterGeo, counterMat);
  counter.position.y = 0.4;
  counter.castShadow = true;
  counter.receiveShadow = true;
  group.add(counter);

  // Coffee machine body (on counter)
  const machineGeo = new THREE.BoxGeometry(0.25, 0.35, 0.2);
  const machine = new THREE.Mesh(machineGeo, machineMat);
  machine.position.set(-0.1, 0.975, 0);
  machine.castShadow = true;
  group.add(machine);

  // Power indicator (small blue emissive dot)
  const dotGeo = new THREE.SphereGeometry(0.015, 8, 8);
  const dotMat = new THREE.MeshStandardMaterial({
    color: 0x4488ff,
    emissive: 0x4488ff,
    emissiveIntensity: 2,
  });
  const dot = new THREE.Mesh(dotGeo, dotMat);
  dot.position.set(-0.1, 0.92, 0.11);
  group.add(dot);

  // Water cooler (cylinder next to counter)
  const coolerGeo = new THREE.CylinderGeometry(0.1, 0.1, 0.45, 12);
  const cooler = new THREE.Mesh(coolerGeo, coolerMat);
  cooler.position.set(0.45, 0.225, 0);
  cooler.castShadow = true;
  group.add(cooler);

  // Water cooler cap
  const capGeo = new THREE.CylinderGeometry(0.11, 0.11, 0.03, 12);
  const cap = new THREE.Mesh(capGeo, machineMat);
  cap.position.set(0.45, 0.46, 0);
  group.add(cap);

  // Small mugs on counter
  const mugMat = new THREE.MeshStandardMaterial({ color: MUG_COLOR, roughness: 0.4 });
  [0.08, 0.2].forEach((x, i) => {
    const mugGeo = new THREE.CylinderGeometry(0.04, 0.035, 0.06, 8);
    const mug = new THREE.Mesh(mugGeo, mugMat);
    mug.position.set(x, 0.83, 0.05);
    group.add(mug);
  });

  return group;
}

// ── 12. Meeting Table ─────────────────────────────────────
export function createMeetingTable(
  position: THREE.Vector3,
  id: string = 'meeting-table',
): THREE.Group {
  const group = new THREE.Group();
  group.name = id;
  group.userData = { id, type: 'furniture' };
  group.position.copy(position);

  const tableMat = new THREE.MeshStandardMaterial({ color: MEETING_TABLE_COLOR });
  const stoolMat = new THREE.MeshStandardMaterial({ color: STOOL_COLOR });

  // Round table top
  const topGeo = new THREE.CylinderGeometry(0.5, 0.5, 0.03, 24);
  const top = new THREE.Mesh(topGeo, tableMat);
  top.position.y = 0.7;
  top.castShadow = true;
  top.receiveShadow = true;
  group.add(top);

  // Table leg (single central)
  const legGeo = new THREE.CylinderGeometry(0.04, 0.06, 0.68, 8);
  const leg = new THREE.Mesh(legGeo, stoolMat);
  leg.position.y = 0.34;
  leg.castShadow = true;
  group.add(leg);

  // Table base (wider foot)
  const baseGeo = new THREE.CylinderGeometry(0.2, 0.2, 0.02, 12);
  const base = new THREE.Mesh(baseGeo, stoolMat);
  base.position.y = 0.01;
  group.add(base);

  // 3 stools around the table
  const stoolAngles = [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3];
  stoolAngles.forEach((angle, i) => {
    const stoolGroup = new THREE.Group();
    const sx = Math.cos(angle) * 0.75;
    const sz = Math.sin(angle) * 0.75;
    stoolGroup.position.set(sx, 0, sz);

    // Seat
    const seatGeo = new THREE.CylinderGeometry(0.12, 0.12, 0.03, 12);
    const seat = new THREE.Mesh(seatGeo, stoolMat);
    seat.position.y = 0.45;
    seat.castShadow = true;
    stoolGroup.add(seat);

    // Stool leg
    const sLegGeo = new THREE.CylinderGeometry(0.02, 0.025, 0.43, 6);
    const sLeg = new THREE.Mesh(sLegGeo, stoolMat);
    sLeg.position.y = 0.215;
    stoolGroup.add(sLeg);

    group.add(stoolGroup);
  });

  return group;
}
